import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useLanguage } from 'contexts/LanguageContext';
import { useCustomers } from 'hooks/useCustomers';
import { useUsers } from 'hooks/useUsers';
import { useProjects } from 'hooks/useProjects';
import { useCustomFields } from 'hooks/useCustomFields';
import CustomFieldsRenderer from 'components/common/CustomFieldsRenderer';
import { Task, TaskFormData, TaskDocument, TaskRecurrence } from 'types/task';
import RecurrenceEditor from './RecurrenceEditor';
import Modal from 'components/common/Modal';
import FormTabs from 'components/common/FormTabs';
import SubtaskList from './SubtaskList';
import { useTaskStatuses } from 'hooks/useTaskStatuses';
import SearchableSelect from 'components/common/SearchableSelect';
import DocumentUpload from 'components/common/DocumentUpload';
import DateTimePicker from 'components/common/DateTimePicker';
import { XMarkIcon, ClockIcon, TagIcon, UserIcon, RectangleStackIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface TaskFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TaskFormData) => void;
  initialData?: Task;
  loading?: boolean;
  preSelectedProjectId?: string; // Pre-select a project when creating tasks from project view
  onExplicitClose?: () => void; // Explicit bezárás függvény
}

const TaskForm: React.FC<TaskFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  loading = false,
  preSelectedProjectId,
  onExplicitClose
}) => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const { users } = useUsers();
  const { projects } = useProjects();
  const { customFields } = useCustomFields('task');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { statuses: statusDefs } = useTaskStatuses();
  type FormTabId = 'basic' | 'assign' | 'extra';
  const [activeTab, setActiveTab] = useState<FormTabId>('basic');
  const [tabErrors, setTabErrors] = useState<string[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const tabCls = (tab: FormTabId) => (activeTab === tab ? 'space-y-2 md:space-y-4' : 'hidden');
  const onInvalid = (errs: Record<string, unknown>) => {
    const tabs = new Set<FormTabId>();
    Object.keys(errs).forEach(name => {
      const el = formRef.current?.querySelector(`[name="${name}"], [name^="${name}."]`);
      const tab = el?.closest('[data-tab]')?.getAttribute('data-tab') as FormTabId | null;
      if (tab) tabs.add(tab);
    });
    const list = Array.from(tabs);
    setTabErrors(list);
    if (list.length > 0 && !list.includes(activeTab)) setActiveTab(list[0]);
  };
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(initialData?.customFields || {});
  const [recurrence, setRecurrence] = useState<TaskRecurrence | null>(initialData?.recurrence || null);

  // Dark mode detection
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  // Form kezelés
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useForm<TaskFormData>({
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      assignedTo: '',
      customerId: '',
      dueDate: undefined,
      estimatedHours: undefined,
      manualHours: undefined,
      actualHours: undefined,
      tags: [],
      documents: []
    }
  });

  // Címkék kezelése
  const [tagInput, setTagInput] = useState('');
  useEffect(() => { if (isOpen) { setActiveTab('basic'); setTabErrors([]); } }, [isOpen]);
  const currentTags = watch('tags') || [];

  // Kiválasztott ügyfél kezelése
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');

  // Kiválasztott projekt kezelése
  const [selectedProject, setSelectedProject] = useState<string>('');

  // Dátum formázás datetime-local input-hoz
  const formatDateTimeForInput = (date: Date | undefined): string => {
    if (!date) return '';

    try {
      // Ha Date objektum
      if (date instanceof Date && !isNaN(date.getTime())) {
        // Helyi időzóna szerinti formátum: YYYY-MM-DDTHH:mm
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      return '';
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return '';
    }
  };

  // Form reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        reset({
          title: initialData.title,
          description: initialData.description || '',
          priority: initialData.priority,
          status: initialData.status,
          statusId: initialData.statusId || initialData.status,
          assignedTo: initialData.assignedTo || '',
          customerId: initialData.customerId || '',
          projectId: initialData.projectId || '',
          dueDate: initialData.dueDate,
          estimatedHours: initialData.estimatedHours,
          manualHours: initialData.manualHours,
          actualHours: initialData.actualHours,
          tags: initialData.tags || [],
          documents: initialData.documents || []
        });
        setSelectedCustomer(initialData.customerId || '');
        setSelectedProject(initialData.projectId || preSelectedProjectId || '');
        setCustomFieldValues(initialData.customFields || {});
        setRecurrence(initialData.isRecurringTemplate && initialData.recurrence ? initialData.recurrence : null);
      } else {
        reset({
          title: '',
          description: '',
          priority: 'medium',
          status: 'todo',
          statusId: statusDefs.find(x => x.category === 'todo')?.id || 'todo',
          assignedTo: '',
          customerId: '',
          projectId: preSelectedProjectId || '',
          dueDate: undefined,
          estimatedHours: undefined,
          manualHours: undefined,
          actualHours: undefined,
          tags: [],
          documents: []
        });
        setSelectedCustomer('');
        setSelectedProject(preSelectedProjectId || '');
        setCustomFieldValues({});
        setRecurrence(null);
      }
      setTagInput('');
    }
  }, [isOpen, initialData, preSelectedProjectId]);

  // Ügyfél kiválasztás kezelése
  const handleCustomerSelect = (customerId: string) => {
    setSelectedCustomer(customerId);
    setValue('customerId', customerId);
  };

  // Projekt kiválasztás kezelése
  const handleProjectSelect = (projectId: string) => {
    setSelectedProject(projectId);
    setValue('projectId', projectId);
  };

  // Címke hozzáadása
  const handleAddTag = () => {
    if (tagInput.trim() && !currentTags.includes(tagInput.trim())) {
      const newTags = [...currentTags, tagInput.trim()];
      setValue('tags', newTags);
      setTagInput('');
    }
  };

  // Címke eltávolítása
  const handleRemoveTag = (tagToRemove: string) => {
    const newTags = currentTags.filter(tag => tag !== tagToRemove);
    setValue('tags', newTags);
  };

  // Enter lenyomás kezelése címkék hozzáadásához
  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Form submit
  const onSubmitHandler = async (data: TaskFormData) => {
    setIsSubmitting(true);

    try {
      // Ügyfél ID hozzáadása
      if (selectedCustomer) {
        data.customerId = selectedCustomer;
      }

      // Projekt ID hozzáadása
      if (selectedProject) {
        data.projectId = selectedProject;
      } else if (preSelectedProjectId) {
        data.projectId = preSelectedProjectId;
      }

      // Egyedi mezők hozzáadása
      data.customFields = customFieldValues;

      // Ismétlődés (sablon) – csak akkor küldjük, ha be van kapcsolva vagy korábban az volt
      if (recurrence || initialData?.isRecurringTemplate) {
        data.recurrence = recurrence;
        data.isRecurringTemplate = !!recurrence;
      }

      await onSubmit(data);

      // Sikeres mentés toast
      if (initialData) {
        toast.success(t('tasks.messages.updateSuccess') || 'Feladat sikeresen frissítve!');
      } else {
        toast.success(t('tasks.messages.createSuccess') || 'Feladat sikeresen létrehozva!');
      }

      // A parent komponens (ProjectTasksModal) kezeli a modal bezárását
    } catch (error) {
      console.error('Error submitting task:', error);
      const errorMessage = initialData 
        ? (t('tasks.messages.updateError') || 'Hiba történt a feladat frissítése során')
        : (t('tasks.messages.createError') || 'Hiba történt a feladat létrehozása során');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ügyfél opciók
  const customerOptions = customers.map(customer => ({
    value: customer.id,
    label: customer.type === 'organization'
      ? customer.companyName
      : `${customer.firstName} ${customer.lastName}`
  }));

  // Projekt opciók (csak aktív projektek)
  const projectOptions = projects
    .filter(project => project.status === 'active' || project.status === 'planning')
    .map(project => ({
      value: project.id,
      label: project.name
    }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      onExplicitClose={() => {
        if (onExplicitClose) {
          onExplicitClose();
        } else {
          onClose();
        }
      }}
      title={initialData ? t('tasks.editTask') : t('tasks.createTask')}
      size="3xl"
      noPadding
      zIndex={50}
    >
      <form noValidate
        ref={formRef}
        onSubmit={handleSubmit(onSubmitHandler, onInvalid)}
        className="space-y-3 md:space-y-4 p-4 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <FormTabs
          tabs={[
            { id: 'basic', label: 'Feladat', icon: ClockIcon },
            { id: 'assign', label: 'Ügyfél, projekt, idő, ismétlődés', icon: UserIcon },
            { id: 'extra', label: 'Címkék, fájlok, egyedi mezők', icon: PaperClipIcon }
          ]}
          active={activeTab}
          onChange={(id) => setActiveTab(id as FormTabId)}
          errorTabs={tabErrors}
        />
        {/* Fix minimum magasság, hogy a fülek váltásakor ne ugráljon az ablak */}
        <div className="bg-white dark:bg-gray-800 md:min-h-[560px]">
          <div data-tab="basic" className={tabCls('basic')}>
            {/* Cím – nagy, keret nélküli, mint a ClickUp-ban */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">{t('tasks.fields.title')} *</div>
                  {initialData?.taskNumber && <span className="text-[11px] font-mono text-gray-400">{initialData.taskNumber}</span>}
                </div>
                <input
                  {...register('title', { required: t('tasks.validation.titleRequired') })}
                  type="text"
                  autoFocus={!initialData}
                  className="w-full h-11 border rounded-lg px-3 text-base md:text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-50 placeholder:font-normal placeholder-gray-400"
                  placeholder="Feladat neve…"
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                />
                {errors.title && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.title.message}</p>}
              </div>

              {/* Tulajdonságsor: státusz, prioritás, határidő, felelős */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">{t('tasks.fields.status')}</div>
                  <input type="hidden" {...register('status')} />
                  <input type="hidden" {...register('statusId')} />
                  <div className="flex flex-wrap gap-1.5">
                    {statusDefs.map(o => {
                      const cur = watch('statusId') || watch('status');
                      const active = cur === o.id || (!watch('statusId') && watch('status') === o.category && statusDefs.find(x => x.category === o.category)?.id === o.id);
                      return (
                        <button key={o.id} type="button" onClick={(e) => { e.stopPropagation(); setValue('status', o.category, { shouldDirty: true }); setValue('statusId', o.id, { shouldDirty: true }); }}
                          style={active ? { backgroundColor: o.color, borderColor: o.color } : undefined}
                          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium transition-colors ${active ? 'text-white' : 'border-[var(--crm-border)] text-gray-700 dark:text-gray-200 hover:border-gray-400'}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: active ? 'rgba(255,255,255,.85)' : o.color }} />{o.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">{t('tasks.fields.priority')}</div>
                  <input type="hidden" {...register('priority')} />
                  <div className="flex flex-wrap gap-1.5">
                    {([
                      { v: 'low', label: t('tasks.priority.low'), on: 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700', dot: 'bg-emerald-500' },
                      { v: 'medium', label: t('tasks.priority.medium'), on: 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700', dot: 'bg-amber-500' },
                      { v: 'high', label: t('tasks.priority.high'), on: 'bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700', dot: 'bg-orange-500' },
                      { v: 'urgent', label: t('tasks.priority.urgent'), on: 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-700', dot: 'bg-rose-500' }
                    ] as { v: Task['priority']; label: string; on: string; dot: string }[]).map(o => {
                      const active = watch('priority') === o.v;
                      return (
                        <button key={o.v} type="button" onClick={(e) => { e.stopPropagation(); setValue('priority', o.v, { shouldDirty: true }); }}
                          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium transition-colors ${active ? o.on + ' ring-2 ring-offset-1 ring-current/20 dark:ring-offset-gray-800' : 'border-[var(--crm-border)] text-gray-700 dark:text-gray-200 hover:border-gray-400'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${o.dot}`} />{o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">{t('tasks.fields.dueDate')}</div>
                  <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
                    <DateTimePicker
                      value={watch('dueDate')}
                      onChange={(date) => setValue('dueDate', date)}
                      placeholder={t('tasks.placeholders.selectDueDate') || 'Válassz határidőt'}
                      showTime={true}
                    />
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {[
                      { label: 'Ma', days: 0 }, { label: 'Holnap', days: 1 }, { label: '+1 hét', days: 7 }
                    ].map(q => (
                      <button key={q.label} type="button" onClick={(e) => { e.stopPropagation(); const d = new Date(); d.setDate(d.getDate() + q.days); d.setHours(17, 0, 0, 0); setValue('dueDate', d, { shouldDirty: true }); }}
                        className="h-6 px-2 rounded-md border border-[var(--crm-border)] text-[11px] text-gray-600 dark:text-gray-300 hover:border-gray-400">{q.label}</button>
                    ))}
                    {watch('dueDate') && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setValue('dueDate', undefined as any, { shouldDirty: true }); }} className="h-6 px-2 rounded-md text-[11px] text-gray-500 hover:text-rose-600">Törlés</button>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">{t('tasks.fields.assignedTo')}</div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <SearchableSelect
                      options={users.map(user => ({ value: user.id, label: `${user.displayName} (${user.email})` }))}
                      value={watch('assignedTo')}
                      onChange={(value) => setValue('assignedTo', value)}
                      placeholder={t('tasks.placeholders.selectAssignee')}
                      searchPlaceholder={t('tasks.placeholders.searchAssignee')}
                    />
                  </div>
                </div>
              </div>

              {/* Leírás */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">{t('tasks.fields.description')}</div>
                <textarea
                  {...register('description')}
                  rows={10}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm leading-relaxed resize-y min-h-[220px] max-h-[520px]"
                  placeholder="Mit kell csinálni, mi a kész definíciója, mire figyelj…"
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                />
                <p className="text-[11px] text-gray-400 mt-1">A sortörések megmaradnak. A címkék, fájlok és egyedi mezők a harmadik fülön.</p>
              </div>

              {/* Alfeladatok */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold mb-1.5">Alfeladatok</div>
                <SubtaskList subtasks={watch('subtasks') || []} onChange={(next) => setValue('subtasks', next, { shouldDirty: true })} />
              </div>
            </div>
          </div>
          <div data-tab="assign" className={tabCls('assign')}>
            {/* Hozzárendelés */}
            <div className="crm-card">
              <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <UserIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('tasks.sections.assignment')}</h3>
          </div>
          <div className="p-4">

                  <div className="space-y-2 md:space-y-4">
                    {/* Ügyfél */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.customer')}
                      </label>
                      <div onClick={(e) => e.stopPropagation()}>
                        <SearchableSelect
                          options={customerOptions}
                          value={selectedCustomer}
                          onChange={handleCustomerSelect}
                          placeholder={t('tasks.placeholders.selectCustomer')}
                          searchPlaceholder={t('tasks.placeholders.searchCustomer')}
                        />
                      </div>
                    </div>

                    {/* Projekt */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.project')}
                      </label>
                      <div onClick={(e) => e.stopPropagation()}>
                        <SearchableSelect
                          options={projectOptions}
                          value={selectedProject}
                          onChange={handleProjectSelect}
                          placeholder={t('tasks.placeholders.selectProject')}
                          searchPlaceholder={t('tasks.placeholders.searchProject')}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Időbecslés */}
            <div className="crm-card">
              <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <ClockIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('tasks.sections.timeEstimation')}</h3>
          </div>
          <div className="p-4">

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                    {/* Becsült órák */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        {t('tasks.fields.estimatedHours')}
                      </label>
                      <input
                        {...register('estimatedHours', {
                          valueAsNumber: true,
                          min: { value: 0, message: t('tasks.validation.estimatedHoursMin') }
                        })}
                        type="number"
                        step="0.5"
                        min="0"
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      />
                      {errors.estimatedHours && (
                        <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.estimatedHours.message}</p>
                      )}
                    </div>

                    {/* Manuális órák */}
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        🖊️ {t('tasks.fields.manualHours') || 'Manuális órák'}
                      </label>
                      <input
                        {...register('manualHours', {
                          valueAsNumber: true,
                          min: { value: 0, message: t('tasks.validation.manualHoursMin') || 'A manuális órák száma nem lehet negatív' }
                        })}
                        type="number"
                        step="0.5"
                        min="0"
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      />
                      {errors.manualHours && (
                        <p className="text-red-600 dark:text-red-400 text-sm mt-1">{errors.manualHours.message}</p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        💡 Gyorsan felvihető tényleges órák (hozzáadódik az időmérős órákhoz)
                      </p>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <RecurrenceEditor value={recurrence} onChange={setRecurrence} hasCustomer={!!selectedCustomer} />
          </div>
          <div data-tab="extra" className={tabCls('extra')}>
            {/* Címkék */}
            <div className="crm-card">
              <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <TagIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('tasks.sections.tags')}</h3>
          </div>
          <div className="p-4">

                  <div className="space-y-2 md:space-y-4">
                    {/* Címke hozzáadás */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={handleTagKeyPress}
                        className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 text-sm text-gray-900 dark:text-gray-100 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        placeholder={t('tasks.placeholders.addTag')}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      />
                      <button
                        type="button"
                        onClick={handleAddTag}
                        className="w-full sm:w-auto px-3 md:px-4 py-1.5 md:py-2 bg-primary-600 text-white text-sm rounded-md md:rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {t('tasks.actions.addTag')}
                      </button>
                    </div>

                    {/* Meglévő címkék */}
                    {currentTags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {currentTags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTag(tag);
                              }}
                              className="ml-1 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Dokumentumok feltöltése */}
            <DocumentUpload
              documents={watch('documents') || []}
              onDocumentsChange={(documents) => setValue('documents', documents)}
              uploadPath={`tasks/${initialData?.id || 'temp'}/documents`}
              title={t('tasks.fields.documents') || 'Dokumentumok'}
              subtitle={t('tasks.placeholders.documents') || 'Csatolja a feladathoz kapcsolódó dokumentumokat'}
              disabled={isSubmitting || loading}
            />

            {/* Egyedi mezők */}
            {customFields.length > 0 && (
              <div className="crm-card">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
                  <RectangleStackIcon className="w-4 h-4 text-gray-400" />
                  <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Egyedi mezők</h3>
                </div>
                <div className="p-4">
                <CustomFieldsRenderer
                  customFields={customFields}
                  values={customFieldValues}
                  onChange={(key, value) => setCustomFieldValues(prev => ({ ...prev, [key]: value }))}
                  disabled={isSubmitting || loading}
                />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Gombok */}
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 md:pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onExplicitClose) {
                onExplicitClose();
              } else {
                onClose();
              }
            }}
            className="w-full sm:w-auto px-3 md:px-4 py-2 md:py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg 
                     hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={isSubmitting || loading}
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();

              // Explicit form submit - nem a native submit event-et használjuk
              const formData = watch();

              // Validáció ellenőrzés
              const isValid = await trigger();
              if (isValid) {
                await onSubmitHandler(formData);
              }
            }}
            className="w-full sm:w-auto px-3 md:px-4 py-2 md:py-2 text-sm font-medium text-white bg-primary-600 rounded-md md:rounded-lg 
                     hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 
                     disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting || loading ? t('common.saving') : (initialData ? t('common.update') : t('common.create'))}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TaskForm; 